import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, CheckCircle2, Loader2, X } from "lucide-react";

import { CpdClaimForm, type CpdStanding } from "./ExamCpdPanel";

/**
 * Shown instead of a dead end when an exam's CPD bar blocks the application.
 *
 * The bar is checked BEFORE payment — deliberately, so nobody buys a sitting
 * their record already rules them out of. But refusing without a way forward
 * left the candidate with a red message and nothing to do: the points they had
 * earned outside the events list were never going to appear on their own.
 *
 * So this offers the same claim the upgrade application does. Claimed points
 * count towards the bar immediately (the API's "effective" CPD is awarded plus
 * pending), which is what makes "claim, then pay" a single sitting at the desk
 * rather than a wait for the secretariat.
 *
 * The second way out is the candidate's own word. The ledger lags reality —
 * points earned at another body's event exist long before a certificate is
 * uploaded and approved — so a candidate who is genuinely qualified can declare
 * it and continue. That declaration is recorded on the application with what
 * the record actually showed at the time, which is what makes it something the
 * secretariat can hold them to rather than a checkbox that waves the bar away.
 *
 * Retry goes back through apply — the server re-reads the standing and decides.
 * The client never assumes a claim was enough.
 */

const fmt = (n: number) => {
  const r = Math.round(Number(n || 0) * 100) / 100;
  return Number.isInteger(r) ? String(r) : String(r);
};

export default function CpdShortfallModal({
  open,
  onClose,
  cpd,
  reason,
  onRetry,
  retrying,
  examTitle,
  canAttest = true,
}: {
  open: boolean;
  onClose: () => void;
  /** The standing the server refused on, carried on the blocker itself. */
  cpd: (CpdStanding & { required?: number | null }) | null;
  reason?: string | null;
  /** Re-runs apply → payment. `attested` carries the candidate's declaration. */
  onRetry: (attested: boolean) => void;
  retrying?: boolean;
  examTitle?: string;
  /**
   * Whether the server offered the declaration on this refusal (`can_attest`).
   * Defaults to true so the way out survives a server that predates the flag.
   */
  canAttest?: boolean;
}) {
  // Set once a certificate goes in, so the retry button explains itself rather
  // than sitting there as an unexplained second chance.
  const [claimed, setClaimed] = useState(false);
  const [attested, setAttested] = useState(false);

  if (!open) return null;

  const required = Number(cpd?.required ?? 0);
  const awarded = Number(cpd?.total ?? 0);
  const pending = Number(cpd?.pending_points ?? 0);
  const effective = Number(cpd?.effective ?? 0);
  const shortfall = Math.max(0, required - effective);
  const met = required > 0 && effective >= required;
  const pctOf = required > 0 ? Math.min(100, Math.round((effective / required) * 100)) : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-xl border bg-white shadow-lg">
        <div className="flex items-start justify-between gap-3 border-b px-5 py-4">
          <div>
            <h3 className="font-semibold text-gray-900">CPD requirement not met</h3>
            <p className="mt-0.5 text-xs text-gray-500">
              {examTitle ? `${examTitle} — ` : ""}
              you can claim CPD here and continue without leaving this page.
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-[70vh] space-y-4 overflow-y-auto px-5 py-4">
          {reason ? (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <p className="text-xs text-amber-900">{reason}</p>
            </div>
          ) : null}

          {/* Where they stand. Awarded and claimed are shown apart, because a
              claim that is later turned down takes its points back out. */}
          <div className="rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium text-gray-900">Your CPD standing</span>
              <span
                className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
                  met
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : "border-amber-200 bg-amber-50 text-amber-800"
                }`}
              >
                {met ? "Minimum met" : `${fmt(shortfall)} short`}
              </span>
            </div>

            <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-100">
              <div
                className={`h-full rounded-full transition-all ${met ? "bg-emerald-500" : "bg-amber-500"}`}
                style={{ width: `${pctOf}%` }}
              />
            </div>

            <div className="mt-3 grid grid-cols-4 gap-2 text-center">
              <Figure label="Required" value={fmt(required)} />
              <Figure label="Confirmed" value={fmt(awarded)} />
              <Figure label="In claims" value={fmt(pending)} />
              <Figure label="Counted" value={fmt(effective)} tone={met ? "good" : "bad"} />
            </div>

            {pending > 0 ? (
              <p className="mt-3 text-[11px] text-gray-500">
                {fmt(pending)} point{pending === 1 ? "" : "s"} sit in {cpd?.pending_count ?? 1}{" "}
                claim{(cpd?.pending_count ?? 1) === 1 ? "" : "s"} awaiting approval. They count
                towards this now — if a claim is turned down they come back out.
              </p>
            ) : null}
          </div>

          {/* The claim itself — the same form and endpoint as the upgrade
              application's top-up, so a claim made here shows on the CPD page
              like any other. */}
          <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-4">
            <p className="text-sm font-medium text-gray-900">Claim CPD you have already earned</p>
            <p className="mt-0.5 text-xs text-gray-600">
              Upload a certificate for CPD earned outside the events list. The points count
              towards this exam straight away and are confirmed on review.
            </p>
            <div className="mt-3">
              <CpdClaimForm onClaimed={() => setClaimed(true)} />
            </div>
            {claimed ? (
              <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-emerald-700">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Claim submitted. Continue below — it counts towards the requirement now.
              </p>
            ) : null}
          </div>

          {/* The second way out. Offered alongside the claim rather than after
              it, because a candidate whose certificates are elsewhere should
              not have to fail at uploading before being told they may declare.
              The wording is the undertaking itself — it is what the secretariat
              will hold them to, so it says what it means. */}
          {canAttest && !met ? (
            <div className="rounded-xl border border-gray-200 p-4">
              <p className="text-sm font-medium text-gray-900">Or confirm you already hold it</p>
              <p className="mt-0.5 text-xs text-gray-600">
                If you have earned the CPD but it is not on your record yet, you may declare it and
                continue to payment.
              </p>

              <label className="mt-3 flex cursor-pointer items-start gap-2 text-xs text-gray-700">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 accent-indigo-600"
                  checked={attested}
                  onChange={(e) => setAttested(e.target.checked)}
                />
                <span>
                  I confirm that I have earned at least {fmt(required)} CPD points, that I will
                  provide evidence of them on request, and that my application may be withdrawn and
                  the fee forfeited if the declaration proves untrue.
                </span>
              </label>
            </div>
          ) : null}

          <p className="text-[11px] text-gray-500">
            Points from events you attended are added automatically. See{" "}
            <Link to="/my-cpd" className="font-medium text-indigo-600 hover:underline">
              My CPD
            </Link>{" "}
            for your full record.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t px-5 py-3">
          <button
            onClick={onClose}
            className="rounded-lg px-3 py-2 text-sm font-medium text-gray-500 hover:text-gray-700"
          >
            Close
          </button>
          {/* Held shut while the candidate is still short and has not declared:
              pressing it would only earn the same refusal, and a button that
              refuses you twice reads as broken rather than as a bar. */}
          <button
            onClick={() => onRetry(attested)}
            disabled={retrying || (!met && !attested && !claimed)}
            title={
              !met && !attested && !claimed
                ? "Claim the CPD you have earned, or confirm you hold it, to continue."
                : undefined
            }
            className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
          >
            {retrying ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {retrying ? "Checking…" : "Continue to payment"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Figure({ label, value, tone }: { label: string; value: string; tone?: "good" | "bad" }) {
  const toneClass =
    tone === "good" ? "text-emerald-700" : tone === "bad" ? "text-amber-700" : "text-gray-900";
  return (
    <div className="rounded-lg bg-gray-50 px-2 py-2">
      <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">{label}</p>
      <p className={`mt-0.5 text-sm font-bold tabular-nums ${toneClass}`}>{value}</p>
    </div>
  );
}
